import { useState, useEffect } from 'react';
import { MessageCircle, Clock, BookOpen, Sparkles, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { listChatSessions, getChatSession } from '@/api/chat';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

/**
 * Chat History Panel Component
 * Shows list of chat sessions that can be resumed
 */
export default function ChatHistoryPanel({ onClose, onSelectSession }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPageContext, setSelectedPageContext] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, [selectedPageContext]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const params = selectedPageContext ? { page_context: selectedPageContext } : {};
      const data = await listChatSessions(params);
      setSessions(data.results || []);
    } catch (error) {
      console.error('Error loading chat sessions:', error);
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (sessionId) => {
    try {
      const session = await getChatSession(sessionId);
      if (onSelectSession) {
        onSelectSession(session);
      } else {
        // Navigate to appropriate page based on page_context
        const contextRoutes = {
          'chat': '/student/chat',
          'learn': '/student/learn',
          'curiosity': '/student/curiosity',
          'curiosity_video': '/student/curiosity/video',
          'curiosity_podcast': '/student/curiosity/podcast',
          'curiosity_content': '/student/curiosity/content',
          'curiosity_news': '/student/curiosity/news',
          'curiosity_news_detail': '/student/curiosity/news'
        };
        
        const route = contextRoutes[session.page_context] || '/student/chat';
        navigate(route, { state: { sessionId: session.session_id } });
      }
      if (onClose) onClose();
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load chat session');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getContextIcon = (pageContext) => {
    switch (pageContext) {
      case 'chat': return MessageCircle;
      case 'learn': return BookOpen;
      case 'curiosity':
      case 'curiosity_video':
      case 'curiosity_podcast':
      case 'curiosity_content':
      case 'curiosity_news':
      case 'curiosity_news_detail':
        return Sparkles;
      default: return MessageCircle;
    }
  };

  const getContextLabel = (pageContext) => {
    const labels = {
      'chat': 'Main Chat',
      'learn': 'Learn Page',
      'curiosity': 'Curiosity Centre',
      'curiosity_video': 'Video',
      'curiosity_podcast': 'Podcast',
      'curiosity_content': 'Other Contents',
      'curiosity_news': 'News',
      'curiosity_news_detail': 'News Detail'
    };
    return labels[pageContext] || pageContext;
  };

  const pageContexts = ['chat', 'learn', 'curiosity', 'curiosity_video', 'curiosity_podcast', 'curiosity_content'];

  return (
    <div className="w-80 bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] flex flex-col h-screen">
      {/* Header */}
      <div className="p-4 border-b border-[hsl(var(--card-border))] flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Chat History</h2>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="p-4 border-b border-[hsl(var(--card-border))] bg-[hsl(var(--sidebar-bg))]">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedPageContext === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPageContext(null)}
            className={cn(
              "text-xs",
              selectedPageContext === null
                ? "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))] border-[hsl(var(--primary))]"
                : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-foreground"
            )}
          >
            All
          </Button>
          {pageContexts.map(context => (
            <Button
              key={context}
              variant={selectedPageContext === context ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPageContext(context)}
              className={cn(
                "text-xs",
                selectedPageContext === context
                  ? "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))] border-[hsl(var(--primary))]"
                  : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-foreground"
              )}
            >
              {getContextLabel(context)}
            </Button>
          ))}
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
              <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-sm text-muted-foreground">No chat history</p>
            <p className="text-xs text-muted-foreground mt-2">Start a conversation to see it here</p>
          </div>
        ) : (
          sessions.map((session) => {
            const ContextIcon = getContextIcon(session.page_context);
            return (
              <Card
                key={session.session_id}
                className={cn(
                  "cursor-pointer transition-all hover:bg-[hsl(var(--sidebar-hover))]",
                  "border-[hsl(var(--card-border))]"
                )}
                onClick={() => handleSessionClick(session.session_id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] flex items-center justify-center flex-shrink-0">
                      <ContextIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-foreground truncate">
                          {session.title || 'Untitled Chat'}
                        </h3>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <span>{getContextLabel(session.page_context)}</span>
                        {session.subject_name && (
                          <>
                            <span>•</span>
                            <span>{session.subject_name}</span>
                          </>
                        )}
                        {session.chapter_name && (
                          <>
                            <span>•</span>
                            <span>{session.chapter_name}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(session.last_message_at)}</span>
                        <span>•</span>
                        <span>{session.message_count || 0} messages</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

